/**
 * Sessiya holati — docs/SECURITY.md §3.
 *
 * Tokenlar XOTIRADA saqlanadi. `localStorage` ishlatilmaydi: u Telegram
 * WebView'da ishonchsiz (docs/TELEGRAM.md) va XSS bo'lsa birinchi o'g'irlanadi.
 * Access token 15 daqiqa, refresh esa `CloudStorage` da.
 *
 * Bu do'kon FAQAT sessiyani ushlaydi. Profil ma'lumoti bu yerda emas —
 * u `useMe()` dan keladi (D-064: "men kimman" bitta joyda).
 */

import type { AuthResponse, TokenPair } from '@voice/api-client';
import { create } from 'zustand';

import { request, setAccessToken } from '../lib/api/client';
import { getInitData, supportsCloudStorage, tg } from './telegram';

const REFRESH_KEY = 'voice.refresh';

/**
 * `CloudStorage` javobini qancha kutamiz.
 *
 * Versiya qo'rig'i (`supportsCloudStorage`) eski klientni to'sadi, bu
 * taymer esa qolgan HAMMA holatni: klient yangi, lekin qo'ng'iroq
 * javobsiz qoldi. Kutishning narxi past — javob kelmasa shunchaki
 * to'liq qayta kirishga tushamiz.
 */
const CLOUD_STORAGE_TIMEOUT_MS = 3000;

/**
 * Brauzerda kirish uchun test foydalanuvchi (docs/LOCAL_DEV.md Yo'l 1).
 *
 * `?dev_user=5` — URL'dan, TAB uchun. 1v1 ni sinash uchun ikkita turli
 * foydalanuvchi kerak, `VITE_DEV_USER_ID` esa build vaqtida qotadi va
 * ikkala tabda bir xil bo'lardi.
 */
function devUserId(): number {
  const fromUrl = new URLSearchParams(window.location.search).get('dev_user');
  return Number(fromUrl ?? import.meta.env['VITE_DEV_USER_ID'] ?? 1);
}

interface AuthState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  isNew: boolean;
  login: () => Promise<void>;
}

function saveRefresh(token: string): void {
  if (!supportsCloudStorage()) return;
  tg?.CloudStorage?.setItem(REFRESH_KEY, token);
}

function forgetRefresh(): void {
  if (!supportsCloudStorage()) return;
  tg?.CloudStorage?.removeItem(REFRESH_KEY);
}

/**
 * `CloudStorage` callback API'sini `Promise` ga o'raydi.
 *
 * ⚠️ **Bu Promise HAR DOIM tugashi shart.** U ilova ishga tushishidagi
 * birinchi `await` va u tugamasa `status` abadiy `loading` bo'lib qoladi:
 * ekranda faqat "Yuklanmoqda…", xato yo'q, qayta urinish tugmasi yo'q.
 * `try/catch` bu holatni ushlamaydi — hech narsa yiqilmaydi, shunchaki
 * hech qachon tugamaydi.
 *
 * Shuning uchun ikki qavat: versiya qo'rig'i (eski klientda callback
 * umuman kelmaydi — `supportsCloudStorage` izohiga qarang) va taymer
 * (qolgan hamma holat uchun).
 *
 * Muvaffaqiyatsizlikning narxi past: `null` qaytadi va odam `initData`
 * bilan to'liq qayta kiradi.
 */
function loadRefresh(): Promise<string | null> {
  const storage = tg?.CloudStorage;
  if (!storage || !supportsCloudStorage()) return Promise.resolve(null);

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), CLOUD_STORAGE_TIMEOUT_MS);
    storage.getItem(REFRESH_KEY, (err, value) => {
      clearTimeout(timer);
      resolve(err ? null : (value ?? null));
    });
  });
}

/**
 * Access token eskirganda sessiyani tiklaydi.
 *
 * **Nega `initData` bilan qayta kirish yetmaydi:** `initData` ning
 * `auth_date` amal qilish muddati bor (`INITDATA_MAX_AGE_SECONDS`, standart
 * 1 soat). Ilova bir soatdan uzoq ochiq tursa u ESKIRADI va `/auth/telegram`
 * rad etadi — foydalanuvchi esa hech narsa qilmagan holda tashqariga
 * chiqarilardi. Refresh token aynan shu holat uchun saqlanardi, lekin
 * hech qachon O'QILMASDI (T-006).
 *
 * Muvaffaqiyatsizlikda `false` qaytaradi va chaqiruvchi to'liq qayta
 * kirishga tushadi.
 */
async function refreshSession(): Promise<boolean> {
  const token = await loadRefresh();
  if (!token) return false;

  try {
    const tokens = await request<TokenPair>('/auth/refresh', {
      method: 'POST',
      body: { refresh_token: token },
      auth: false,
    });
    setAccessToken(tokens.access_token);
    saveRefresh(tokens.refresh_token);
    return true;
  } catch {
    // Refresh ham eskirgan yoki bekor qilingan — to'liq qayta kirish.
    forgetRefresh();
    return false;
  }
}

/**
 * Telegramsiz kirish — FAQAT dev build'da (docs/LOCAL_DEV.md Yo'l 1).
 *
 * `import.meta.env.DEV` ni Vite build vaqtida `false` ga almashtiradi va
 * butun shox production bundle'dan CHIQIB KETADI. Ya'ni bu kod ishlab
 * chiqarishga umuman yetib bormaydi.
 *
 * Ikkinchi qavat serverda: `/auth/dev` faqat `ENV=development` da
 * ro'yxatdan o'tadi va `DEV_AUTH_BYPASS` talab qiladi (D-027). Bitta
 * qavat yetmaydi — jim ishlab ketish eng xavfli ssenariy.
 */
async function devLogin(): Promise<void> {
  const tokens = await request<TokenPair>('/auth/dev', {
    method: 'POST',
    body: { user_id: devUserId() },
    auth: false,
  });
  setAccessToken(tokens.access_token);
}

export const useAuth = create<AuthState>((set) => ({
  status: 'idle',
  error: null,
  isNew: false,

  login: async () => {
    set({ status: 'loading', error: null });
    try {
      // Avval SAQLANGAN sessiyani tiklashga urinamiz — u arzonroq va
      // `initData` eskirgan bo'lsa yagona yo'l.
      if (await refreshSession()) {
        set({ status: 'ready' });
        return;
      }

      const initData = getInitData();

      if (!initData) {
        if (!import.meta.env.DEV) {
          set({ status: 'error', error: 'NO_INIT_DATA' });
          return;
        }
        await devLogin();
        set({ status: 'ready' });
        return;
      }

      const auth = await request<AuthResponse>('/auth/telegram', {
        method: 'POST',
        body: { init_data: initData },
        auth: false,
      });

      setAccessToken(auth.access_token);
      saveRefresh(auth.refresh_token);
      set({ status: 'ready', isNew: auth.is_new });
    } catch (err) {
      set({
        status: 'error',
        error: err instanceof Error ? err.message : 'UNKNOWN',
      });
    }
  },
}));
